// Parallelbuchhaltung (INTERN / VORLAEUFIG): eigene Buchungen aus Bankumsaetzen und Belegen, strikt getrennt von allen bisherigen Daten.
// Nichts davon geht an DATEV, den Steuerberater oder das Finanzamt (es gibt dafuer keinen Weg). Rechnungslegung (EUeR/Bilanz) und Versteuerung (Ist/Soll)
// werden NICHT angenommen, sondern hier ausdruecklich festgelegt. Unsichere Buchungen sind rot gekennzeichnet und gelten nie automatisch als korrekt.
import { Fragment, useState, useEffect, useCallback } from "react";
import THEME from "../../../../theme";
import { fs } from "../../utils/styles";
import { pbApi } from "../../../belege/belegeApi";
import { Karte, Klein, Chip, Bestaetigung, euro, datum, datumZeit, useAktion } from "../../../belege/ui";

const SICHERHEIT = { sicher: ["belegt", THEME.colors.success], wahrscheinlich: ["wahrscheinlich", THEME.colors.warning], unsicher: ["UNSICHER", THEME.colors.danger] };
const GRUND = { nur_kanzlei: "bleibt bei der Kanzlei", keine_buchung: "keine Buchung nötig", neutral: "neutral", ignoriert: "ignoriert" };
const AKTION = { buchen: "Buchen", nur_kanzlei: "Nur Kanzlei (nicht buchen)", keine_buchung: "Keine Buchung" };
const heute = () => new Date().toISOString().slice(0, 10);
const monatsanfang = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const zahl = (s) => Number(s).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function SicherheitChip({ wert }) {
  const [text, farbe] = SICHERHEIT[wert] || [wert, THEME.colors.border];
  return <Chip text={text} farbe={farbe} />;
}

// ------------------------------------------------------------------ Einstellungen
export function Einstellungen({ st, neuLaden, setFehler, setHinweis }) {
  const aktion = useAktion(setFehler, setHinweis);
  const e = st.einstellungen;
  const gesperrt = (st.buchungen?.aktiv || 0) > 0;
  const [konten, setKonten] = useState({});
  const setze = (k, v) => aktion(() => pbApi.einstellung(k, v), "Gespeichert.").then(neuLaden);
  const wahl = (k, label, optionen, hilfe) => (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={fs.label}>{label}</span>
      <select style={{ ...fs.input, width: "100%", maxWidth: 360 }} value={e[k]} disabled={gesperrt} onChange={ev => setze(k, ev.target.value)}>
        {!e[k] && <option value="">— noch nicht festgelegt —</option>}
        {optionen.map(([w, t]) => <option key={w} value={w}>{t}</option>)}
      </select>
      {hilfe && <Klein>{hilfe}</Klein>}
    </label>
  );
  const KONTO = [["konto_bank", "Bank"], ["konto_klaerung", "Klärungskonto (unklare Buchungen)"], ["konto_verbindlichkeiten", "Verbindlichkeiten (nur Bilanz)"],
    ["konto_privatentnahme", "Privatentnahme"], ["konto_privateinlage", "Privateinlage"], ["konto_vorsteuer_19", "Vorsteuer 19 %"], ["konto_vorsteuer_7", "Vorsteuer 7 %"],
    ["konto_ust_19", "Umsatzsteuer 19 %"], ["konto_ust_7", "Umsatzsteuer 7 %"]];
  return (
    <div>
      <Karte titel="Buchungsmethode (wird nicht angenommen)" rechts={<Chip text={st.konfiguriert ? `festgelegt: ${st.methode}` : "NICHT festgelegt – es wird nichts gebucht"} farbe={st.konfiguriert ? THEME.colors.success : THEME.colors.danger} />}>
        {wahl("rechnungslegung", "Rechnungslegung", [["eur", "Einnahmen-Überschuss-Rechnung (EÜR)"], ["bilanz", "Bilanzierung"]], "EÜR: Buchung zum Zahlungsdatum. Bilanz: Rechnung zum Rechnungsdatum, Zahlung getrennt. Bitte mit der Kanzlei klären.")}
        {wahl("versteuerung", "Umsatzsteuer", [["ist", "Istversteuerung (nach Zahlung)"], ["soll", "Sollversteuerung (nach Rechnung)"]], "Bei Sollversteuerung sind Erlöse aus reinen Zahlungseingängen immer „unsicher“.")}
        {wahl("datumsregel", "Datum der Bankbuchung", [["buchungsdatum", "Buchungsdatum der Bank"], ["wertstellung", "Wertstellungsdatum"]])}
        {gesperrt && <Klein farbe={THEME.colors.warning}>Die Methode ist gesperrt, solange vorläufige Buchungen existieren. Zum Ändern unter „Buchungslauf“ den Zeitraum zurücksetzen (storniert, nichts wird gelöscht).</Klein>}
      </Karte>
      <Karte titel="Konten (Vorbelegung aus den Kanzleidaten, SKR03)">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          {KONTO.map(([k, t]) => (
            <label key={k}><span style={fs.label}>{t}</span>
              <div style={{ display: "flex", gap: 6 }}>
                <input style={{ ...fs.input, width: 90 }} value={konten[k] ?? e[k]} onChange={ev => setKonten({ ...konten, [k]: ev.target.value })} />
                <button style={fs.btnGray} disabled={(konten[k] ?? e[k]) === e[k]} onClick={() => setze(k, konten[k]).then(() => setKonten({ ...konten, [k]: undefined }))}>Speichern</button>
              </div>
            </label>
          ))}
        </div>
        {(st.konten_hinweise || []).map((h, i) => <Klein key={i} farbe={THEME.colors.warning}>⚠️ {h}</Klein>)}
      </Karte>
    </div>
  );
}

// ------------------------------------------------------------------ Zuordnung Bankkategorie -> Konto
export function Zuordnung({ setFehler, setHinweis }) {
  const aktion = useAktion(setFehler, setHinweis);
  const [d, setD] = useState(null);
  const laden = useCallback(() => { pbApi.zuordnung().then(setD).catch(e => setFehler(e.message)); }, [setFehler]);
  useEffect(() => { laden(); }, [laden]);
  if (!d) return <div>Lädt…</div>;
  return (
    <div>
      <Karte titel="Bankkategorie → Konto" rechts={<button style={fs.btn} onClick={() => aktion(() => pbApi.vorschlaege(), r => `${r.neu} Vorschläge angelegt (unbestätigt).`).then(laden)}>Vorschläge anlegen</button>}>
        <Klein>Buchungen ohne Beleg richten sich nach dieser Zuordnung. <b>Unbestätigte Zuordnungen machen die Buchung „unsicher“.</b> Lohn, Sozialversicherung, Steuern und Sonderbuchungen auf „Nur Kanzlei“ stellen – sie werden nicht gebucht.</Klein>
      </Karte>
      {d.zuordnungen.map(z => <ZuordnungKarte key={z.kategorie_id} z={z} aktion={aktion} neuLaden={laden} />)}
      {d.ohne_zuordnung.length > 0 && (
        <Karte titel={`Kategorien mit Umsätzen, aber ohne Zuordnung (${d.ohne_zuordnung.length})`}>
          <Klein>Diese Umsätze landen auf dem Klärungskonto („unsicher“).</Klein>
          {d.ohne_zuordnung.map(k => (
            <div key={k.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", borderTop: `1px solid ${THEME.colors.border}` }}>
              <div style={{ flex: 1 }}>{k.name} <Klein>{k.kategorie_typ} · {k.umsaetze} Umsätze</Klein></div>
              <button style={fs.btnGray} onClick={() => aktion(() => pbApi.zuordnungSetzen({ kategorie_id: k.id, aktion: "buchen", steuerart: "unbekannt", bestaetigen: false })).then(laden)}>Zuordnung anlegen</button>
            </div>
          ))}
        </Karte>
      )}
    </div>
  );
}

function ZuordnungKarte({ z, aktion, neuLaden }) {
  const [f, setF] = useState({ aktion: z.aktion, konto: z.konto || "", steuerart: z.steuerart, steuersatz: z.steuersatz ?? "", hinweis: z.hinweis || "" });
  const geaendert = f.aktion !== z.aktion || f.konto !== (z.konto || "") || f.steuerart !== z.steuerart || String(f.steuersatz) !== String(z.steuersatz ?? "");
  const speichern = (bestaetigen) => aktion(() => pbApi.zuordnungSetzen({ kategorie_id: z.kategorie_id, aktion: f.aktion, konto: f.konto || null, steuerart: f.steuerart,
    steuersatz: f.steuersatz === "" ? null : Number(f.steuersatz), bestaetigen, hinweis: f.hinweis || null }), bestaetigen ? "Bestätigt." : "Gespeichert (unbestätigt).").then(neuLaden);
  const bucht = f.aktion === "buchen";
  return (
    <Karte stil={{ padding: "0.9rem 1.1rem", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <strong>{z.kategorie_name}</strong>
        <span style={{ display: "flex", gap: 6 }}>
          <Chip text={z.bestaetigt ? "bestätigt" : "unbestätigt → Buchungen unsicher"} farbe={z.bestaetigt ? THEME.colors.success : THEME.colors.danger} />
          <Chip text={`${z.umsaetze} Umsätze`} farbe={THEME.colors.textMuted} />
        </span>
      </div>
      {z.hinweis && <Klein>{z.hinweis}</Klein>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 8, marginTop: 8 }}>
        <select style={fs.input} value={f.aktion} onChange={e => setF({ ...f, aktion: e.target.value })}>{Object.entries(AKTION).map(([k, t]) => <option key={k} value={k}>{t}</option>)}</select>
        <input style={fs.input} placeholder={bucht ? "Konto (leer = Klärung)" : "–"} disabled={!bucht} value={f.konto} onChange={e => setF({ ...f, konto: e.target.value })} />
        <select style={fs.input} disabled={!bucht} value={f.steuerart} onChange={e => setF({ ...f, steuerart: e.target.value })}>
          <option value="unbekannt">Steuer unbekannt</option><option value="vorsteuer">Vorsteuer</option><option value="umsatzsteuer">Umsatzsteuer</option><option value="keine">Keine Steuer</option>
        </select>
        <select style={fs.input} disabled={!bucht || !["vorsteuer", "umsatzsteuer"].includes(f.steuerart)} value={f.steuersatz} onChange={e => setF({ ...f, steuersatz: e.target.value })}>
          <option value="">Satz</option><option value="19">19 %</option><option value="7">7 %</option><option value="0">0 %</option>
        </select>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        <button style={fs.btn} disabled={z.bestaetigt && !geaendert} onClick={() => speichern(true)}>{geaendert ? "Speichern und bestätigen" : "Bestätigen"}</button>
        {geaendert && <button style={fs.btnGray} onClick={() => speichern(false)}>Nur speichern (unbestätigt)</button>}
        <button style={fs.btnDanger} onClick={() => aktion(() => pbApi.zuordnungLoeschen(z.kategorie_id)).then(neuLaden)}>Entfernen</button>
      </div>
    </Karte>
  );
}

// ------------------------------------------------------------------ Lauf
export function Lauf({ st, neuLaden, setFehler, setHinweis }) {
  const aktion = useAktion(setFehler, setHinweis);
  const [von, setVon] = useState(monatsanfang());
  const [bis, setBis] = useState(heute());
  const [erg, setErg] = useState(null);
  const [laeufe, setLaeufe] = useState([]);
  const [frage, setFrage] = useState(null);
  const ladeLaeufe = useCallback(() => { pbApi.laeufe().then(setLaeufe).catch(() => {}); }, []);
  useEffect(() => { ladeLaeufe(); }, [ladeLaeufe]);
  const los = (modus, auch_geprueft = false) => aktion(() => pbApi.lauf({ von, bis, modus, auch_geprueft }), null).then(r => { if (r) { setErg(r); ladeLaeufe(); neuLaden(); } });
  return (
    <div>
      <Karte titel="Buchungen erzeugen">
        {!st.konfiguriert && <Klein farbe={THEME.colors.danger}>Zuerst unter „Einstellungen“ Rechnungslegung und Umsatzsteuer festlegen – bis dahin wird nichts gebucht.</Klein>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end", marginTop: 8 }}>
          <label><span style={fs.label}>Von</span><input type="date" style={fs.input} value={von} onChange={e => setVon(e.target.value)} /></label>
          <label><span style={fs.label}>Bis</span><input type="date" style={fs.input} value={bis} onChange={e => setBis(e.target.value)} /></label>
          <button style={fs.btnGray} disabled={!st.konfiguriert} onClick={() => los("trocken")}>Trockenlauf (zeigt nur)</button>
          <button style={fs.btn} disabled={!st.konfiguriert} onClick={() => los("erzeugen")}>Buchungen erzeugen</button>
          <button style={fs.btnDanger} onClick={() => setFrage("zurueck")}>Zeitraum zurücksetzen</button>
        </div>
        <Klein>Wiederholbar: unveränderte Buchungen bleiben, geänderte werden ersetzt (die alte bleibt als „ersetzt“ erhalten), von Hand geprüfte Buchungen werden nie überschrieben (Konflikt). Gelöscht wird nichts.</Klein>
      </Karte>
      {erg && (
        <Karte titel={`Ergebnis (${erg.modus === "trocken" ? "Trockenlauf – nichts gespeichert" : erg.modus === "zuruecksetzen" ? "zurückgesetzt" : "erzeugt"})`}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Chip text={`${erg.neu || 0} neu`} farbe={THEME.colors.success} /><Chip text={`${erg.unveraendert || 0} unverändert`} farbe={THEME.colors.textMuted} />
            <Chip text={`${erg.ersetzt || 0} ersetzt`} farbe={THEME.colors.accent} /><Chip text={`${erg.storniert || 0} storniert`} farbe={THEME.colors.warning} />
            {erg.konflikte > 0 && <Chip text={`${erg.konflikte} KONFLIKTE`} farbe={THEME.colors.danger} />}
            {erg.modus !== "zuruecksetzen" && <Chip text={`${erg.unsicher || 0} unsicher`} farbe={THEME.colors.danger} />}
            {erg.modus !== "zuruecksetzen" && <Chip text={`${erg.ausnahmen || 0} nicht gebucht (Kanzlei/neutral)`} farbe={THEME.colors.textMuted} />}
          </div>
          {(erg.konfliktliste || []).map((k, i) => <Klein key={i} farbe={THEME.colors.danger}>⚠️ {k.schluessel.join(" / ")}: {k.grund}</Klein>)}
        </Karte>
      )}
      <Karte titel="Letzte Läufe">
        {laeufe.map(l => <Klein key={l.id}>#{l.id} · {datumZeit(l.gestartet_am)} · {l.modus} · {datum(l.von)}–{datum(l.bis)} · neu {l.neu}, ersetzt {l.ersetzt}, storniert {l.storniert}, Konflikte {l.konflikte}</Klein>)}
        {laeufe.length === 0 && <Klein>Noch keine Läufe.</Klein>}
      </Karte>
      {frage === "zurueck" && <Bestaetigung titel="Zeitraum zurücksetzen?" gefahr okText="Zurücksetzen" text={`Alle vorläufigen Buchungen vom ${datum(von)} bis ${datum(bis)} werden storniert (nicht gelöscht). Von Hand geprüfte Buchungen bleiben bestehen. Danach kann die Methode geändert werden.`}
        onAbbruch={() => setFrage(null)} onOk={() => { setFrage(null); los("zuruecksetzen"); }} />}
    </div>
  );
}

// ------------------------------------------------------------------ Buchungen
export function Buchungen({ setFehler, setHinweis, neuLaden, start }) {
  const aktion = useAktion(setFehler, setHinweis);
  const [f, setF] = useState({ von: "", bis: "", sicherheit: "", ungeprueft: false, q: "", status: "vorlaeufig", ...(start || {}) });
  const [d, setD] = useState(null);
  const [seite, setSeite] = useState(0);
  const [frage, setFrage] = useState(null);
  const laden = useCallback(() => {
    pbApi.buchungen({ von: f.von, bis: f.bis, sicherheit: f.sicherheit, ungeprueft: f.ungeprueft ? 1 : "", q: f.q, status: f.status, limit: 50, offset: seite * 50 }).then(setD).catch(e => setFehler(e.message));
  }, [f, seite, setFehler]);
  useEffect(() => { laden(); }, [laden]);
  const pruefen = (b, p) => aktion(() => pbApi.pruefen(b.id, { geprueft: true, notiz: p?.grund || null }), "Als geprüft markiert.").then(() => { laden(); neuLaden(); });
  return (
    <div>
      <Karte titel="Filter">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end" }}>
          <input type="date" aria-label="Von" style={fs.input} value={f.von} onChange={e => { setSeite(0); setF({ ...f, von: e.target.value }); }} />
          <input type="date" aria-label="Bis" style={fs.input} value={f.bis} onChange={e => { setSeite(0); setF({ ...f, bis: e.target.value }); }} />
          <select style={fs.input} value={f.sicherheit} onChange={e => { setSeite(0); setF({ ...f, sicherheit: e.target.value }); }}>
            <option value="">alle Sicherheitsstufen</option><option value="unsicher">nur UNSICHER</option><option value="wahrscheinlich">wahrscheinlich</option><option value="sicher">belegt</option>
          </select>
          <select style={fs.input} value={f.status} onChange={e => { setSeite(0); setF({ ...f, status: e.target.value }); }}>
            <option value="vorlaeufig">aktuelle</option><option value="ersetzt">ersetzte</option><option value="storniert">stornierte</option>
          </select>
          <input style={fs.input} placeholder="Suche (Text, Beleg, Hinweis)" value={f.q} onChange={e => { setSeite(0); setF({ ...f, q: e.target.value }); }} />
          <label><input type="checkbox" checked={f.ungeprueft} onChange={e => { setSeite(0); setF({ ...f, ungeprueft: e.target.checked }); }} /> nur ungeprüft</label>
        </div>
      </Karte>
      {!d ? <div>Lädt…</div> : (
        <>
          <Klein>{d.gesamt} Buchungen</Klein>
          {d.buchungen.map(b => (
            <Karte key={b.id} stil={{ padding: "0.8rem 1rem", marginBottom: 8, borderLeft: `4px solid ${(SICHERHEIT[b.sicherheit] || [])[1] || THEME.colors.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <div><strong>{datum(b.datum)}</strong> · {b.buchungstext || "–"} {b.belegfeld ? <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>· Beleg-Nr. {b.belegfeld}</span> : null}</div>
                <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <SicherheitChip wert={b.sicherheit} />
                  {b.status !== "vorlaeufig" && <Chip text={b.status} farbe={THEME.colors.textMuted} />}
                  <Chip text={b.geprueft_am ? "geprüft" : "ungeprüft"} farbe={b.geprueft_am ? THEME.colors.success : THEME.colors.textMuted} />
                  <Chip text="vorläufig" farbe={THEME.colors.warning} />
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "2px 14px", marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
                {b.zeilen.map((z, i) => (<Fragment key={i}><span>{z.konto} <span style={{ color: THEME.colors.textMuted, fontSize: THEME.fontSize.sm }}>{z.rolle}</span></span><span style={{ textAlign: "right" }}>{Number(z.soll) ? `S ${zahl(z.soll)}` : ""}</span><span style={{ textAlign: "right" }}>{Number(z.haben) ? `H ${zahl(z.haben)}` : ""}</span></Fragment>))}
              </div>
              {b.hinweis && <Klein farbe={b.sicherheit === "unsicher" ? THEME.colors.danger : undefined}>ℹ️ {b.hinweis}</Klein>}
              {b.notiz && <Klein>Notiz: {b.notiz}</Klein>}
              {b.status === "vorlaeufig" && (
                <div style={{ marginTop: 6 }}>
                  {b.geprueft_am
                    ? <button style={fs.btnGray} onClick={() => aktion(() => pbApi.pruefen(b.id, { geprueft: false }), "Prüfvermerk entfernt.").then(() => { laden(); neuLaden(); })}>Prüfvermerk entfernen</button>
                    : <button style={fs.btn} onClick={() => b.sicherheit === "unsicher" ? setFrage(b) : pruefen(b)}>Als geprüft markieren</button>}
                </div>
              )}
            </Karte>
          ))}
          {d.buchungen.length === 0 && <Klein>Keine Buchungen für diesen Filter.</Klein>}
          <div style={{ display: "flex", gap: 8 }}>
            <button style={fs.btnGray} disabled={seite === 0} onClick={() => setSeite(seite - 1)}>← Zurück</button>
            <button style={fs.btnGray} disabled={(seite + 1) * 50 >= d.gesamt} onClick={() => setSeite(seite + 1)}>Weiter →</button>
          </div>
        </>
      )}
      {frage && <Bestaetigung titel="Unsichere Buchung als geprüft markieren?" grundPflicht={5} okText="Als geprüft markieren" text={`Diese Buchung ist als UNSICHER gekennzeichnet:\n${frage.hinweis || ""}\n\nBitte begründen, warum sie nach deiner Prüfung stimmt.`}
        onAbbruch={() => setFrage(null)} onOk={(p) => { const b = frage; setFrage(null); pruefen(b, p); }} />}
    </div>
  );
}

// ------------------------------------------------------------------ Uebersicht
export function Uebersicht({ setFehler }) {
  const [von, setVon] = useState(monatsanfang());
  const [bis, setBis] = useState(heute());
  const [d, setD] = useState(null);
  useEffect(() => { pbApi.uebersicht(von, bis).then(setD).catch(e => setFehler(e.message)); }, [von, bis, setFehler]);
  const kachel = (titel, wert, farbe, hilfe) => (
    <div style={{ border: `1px solid ${farbe || THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "10px 14px" }}>
      <div style={{ fontSize: THEME.fontSize.sm, color: THEME.colors.textSecondary }}>{titel}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: farbe || THEME.colors.text }}>{wert}</div>
      {hilfe && <Klein>{hilfe}</Klein>}
    </div>
  );
  return (
    <div>
      <Karte titel="Zeitraum">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input type="date" aria-label="Von" style={fs.input} value={von} onChange={e => setVon(e.target.value)} />
          <input type="date" aria-label="Bis" style={fs.input} value={bis} onChange={e => setBis(e.target.value)} />
        </div>
      </Karte>
      {d && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10, marginBottom: 16 }}>
            {kachel("Buchungen (aktuell)", d.buchungen.n)}
            {kachel("davon UNSICHER", d.buchungen.unsicher, d.buchungen.unsicher ? THEME.colors.danger : undefined, "müssen geklärt werden")}
            {kachel("wahrscheinlich", d.buchungen.wahrscheinlich, d.buchungen.wahrscheinlich ? THEME.colors.warning : undefined)}
            {kachel("belegt", d.buchungen.sicher, THEME.colors.success)}
            {kachel("ungeprüft", d.buchungen.ungeprueft)}
            {kachel(`Klärungskonto ${d.klaerungskonto.konto}`, d.klaerungskonto.buchungen, d.klaerungskonto.buchungen ? THEME.colors.danger : undefined, `Summe ${euro(d.klaerungskonto.summe)}`)}
            {kachel("Bankumsätze noch ohne Buchung", d.bankumsaetze_ohne_buchung, d.bankumsaetze_ohne_buchung ? THEME.colors.warning : undefined, "Lauf starten oder Zuordnung ergänzen")}
            {kachel("Belege ohne Bankzahlung", d.belege_ohne_bankzahlung, undefined, "in der EÜR noch nicht gebucht")}
            {kachel("Zugeordnet, aber ohne Kontierung", d.belege_ohne_kontierung_mit_bank)}
          </div>
          <Karte titel="Nicht gebucht (bleibt bei der Kanzlei bzw. neutral)">
            {d.ausnahmen.map(a => <div key={a.grund} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}><span>{GRUND[a.grund] || a.grund}</span><span>{a.n} Umsätze · {euro(a.summe)}</span></div>)}
            {d.ausnahmen.length === 0 && <Klein>Keine.</Klein>}
          </Karte>
          {(d.hinweise || []).map((h, i) => <Klein key={i} farbe={THEME.colors.warning}>⚠️ {h}</Klein>)}
        </>
      )}
    </div>
  );
}
